import { memo, useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ChevronRight, ExclamationCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons';

import { useAppSelector } from '../../../../../hooks/redux';

import {

  selectScheduleConflicts,

  selectScheduleConflictCount,

} from '../../../../../features/schedule/scheduleConflictSelectors';

import {

  selectScheduleCurrentFilters,

  selectScheduleData,

  selectScheduleTournamentInfo,

  selectScheduleClassIdToObject,

} from '../../../../../features/schedule/scheduleSelectors';

import type { ScheduleConflict } from '../../../../../features/schedule/scheduleConflictDetector';
import { mergeOverlapsForDisplay, formatScheduleConflictMessage } from '../../../../../features/schedule/scheduleConflictDisplay';

import { flattenScheduledMatches } from '../../../../../features/schedule/scheduleOverlap';

import {

  getHhmmTimeString,

  getTournamentTimezone,

  hasScheduleOccupancyFilters,

} from '../schedule.helpers';

import type { IMatchScheduleItem } from '../../../../../types/schedule';

import { ScheduleConflictDetailModal } from './ScheduleConflictDetailModal';

import './ScheduleConflictsPanel.scss';



type ScheduleConflictsPanelProps = {

  selectedDayKey: number;

  onSelectedDayKeyChange: (dayKey: number) => void;

  onFocusConflictOnGrid: (conflict: ScheduleConflict) => void;

  onViewConflictMatch: (matchId: number) => void;

};



function formatMatchSlot(

  match: IMatchScheduleItem | undefined,

  timeZone: string

): string {

  if (!match?.scheduleDate) return '—';

  const time = getHhmmTimeString(match.scheduleDate, timeZone);

  const court = match.court?.name ?? '—';

  return `${time} · ${court}`;

}



function conflictClassId(

  conflict: ScheduleConflict,

  matchById: Map<number, IMatchScheduleItem>

): number | null {

  const match = matchById.get(conflict.matchIds[0]);

  if (match?.classId != null) return Number(match.classId);

  if (

    conflict.kind === 'BRACKET_ORDERING' ||

    conflict.kind === 'BOX_ROUND_ORDERING' ||

    conflict.kind === 'PARENT_CLASS_ORDERING'

  ) {

    return Number(conflict.entityId);

  }

  return null;

}



export const ScheduleConflictsPanel = memo(function ScheduleConflictsPanel({

  selectedDayKey,

  onSelectedDayKeyChange,

  onFocusConflictOnGrid,

  onViewConflictMatch,

}: ScheduleConflictsPanelProps) {

  const { t } = useTranslation();

  const prefix = 'creativeDetail-';

  const rawConflicts = useAppSelector(selectScheduleConflicts);
  const conflicts = useMemo(() => mergeOverlapsForDisplay(rawConflicts), [rawConflicts]);

  const totalConflictCount = useAppSelector(selectScheduleConflictCount);

  const schedule = useAppSelector(selectScheduleData);

  const currentFilters = useAppSelector(selectScheduleCurrentFilters);

  const tournamentInfo = useAppSelector(selectScheduleTournamentInfo);

  const classIdToObject = useAppSelector(selectScheduleClassIdToObject);

  const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);



  const [selectedConflict, setSelectedConflict] = useState<ScheduleConflict | null>(null);

  const [filterClassIds, setFilterClassIds] = useState<number[] | null>(null);



  const matchById = useMemo(() => {

    const map = new Map<number, IMatchScheduleItem>();

    for (const m of flattenScheduledMatches(schedule)) {

      map.set(m.id, m);

    }

    return map;

  }, [schedule]);



  const classFilterOptions = useMemo(() => {

    const byId = new Map<number, string>();

    for (const conflict of conflicts) {

      const classId = conflictClassId(conflict, matchById);

      if (classId == null) continue;

      if (byId.has(classId)) continue;

      const classObject =

        classIdToObject[String(classId)] ?? classIdToObject[classId];

      byId.set(classId, classObject?.name ?? conflict.entityLabel);

    }

    return [...byId.entries()].sort(([, a], [, b]) => a.localeCompare(b));

  }, [conflicts, matchById, classIdToObject]);



  const filteredConflicts = useMemo(() => {

    if (filterClassIds == null || filterClassIds.length === 0) return conflicts;

    const allowed = new Set(filterClassIds);

    return conflicts.filter((c) => {

      const classId = conflictClassId(c, matchById);

      return classId != null && allowed.has(classId);

    });

  }, [conflicts, filterClassIds, matchById]);



  const displayCount = filterClassIds?.length ? filteredConflicts.length : conflicts.length;



  const groupedByDay = useMemo(() => {

    const groups = new Map<number, ScheduleConflict[]>();

    for (const conflict of filteredConflicts) {

      const list = groups.get(conflict.dayKey) ?? [];

      list.push(conflict);

      groups.set(conflict.dayKey, list);

    }

    return [...groups.entries()].sort(([a], [b]) => a - b);

  }, [filteredConflicts]);



  const filterNotice = hasScheduleOccupancyFilters(currentFilters);



  const handleRowClick = useCallback((conflict: ScheduleConflict) => {

    setSelectedConflict(conflict);

  }, []);



  const handleModalFocusOnGrid = useCallback(

    (conflict: ScheduleConflict) => {

      setSelectedConflict(null);

      onFocusConflictOnGrid(conflict);

    },

    [onFocusConflictOnGrid]

  );



  const toggleClassFilter = useCallback((classId: number) => {

    setFilterClassIds((prev) => {

      if (prev == null) return [classId];

      if (prev.includes(classId)) {

        const next = prev.filter((id) => id !== classId);

        return next.length === 0 ? null : next;

      }

      return [...prev, classId];

    });

  }, []);



  if (totalConflictCount === 0) {

    return (

      <div className="schedule-conflicts-panel schedule-conflicts-panel--empty">

        <p className="small text-muted mb-0">{t(prefix + 'scheduleConflictsEmpty')}</p>

      </div>

    );

  }



  return (

    <div className="schedule-conflicts-panel" role="region" aria-label={t(prefix + 'scheduleConflictsTab')}>

      <div className="schedule-conflicts-panel__header">

        <span className="schedule-conflicts-panel__badge badge bg-dark">{displayCount}</span>

        <span className="fw-semibold small">

          {t(prefix + 'scheduleWarningsPanelSummary', { count: displayCount })}

        </span>

      </div>



      {classFilterOptions.length > 1 ? (

        <div

          className="schedule-conflicts-panel__filters mb-2"

          role="group"

          aria-label={t(prefix + 'scheduleConflictsFilterLabel')}

        >

          <button

            type="button"

            className={`schedule-conflicts-panel__filter-chip btn btn-sm ${

              filterClassIds == null ? 'btn-primary' : 'btn-outline-secondary'

            }`}

            onClick={() => setFilterClassIds(null)}

          >

            {t(prefix + 'scheduleConflictsFilterAll')}

          </button>

          {classFilterOptions.map(([classId, label]) => (

            <button

              key={classId}

              type="button"

              className={`schedule-conflicts-panel__filter-chip btn btn-sm ${

                filterClassIds?.includes(classId) ? 'btn-primary' : 'btn-outline-secondary'

              }`}

              onClick={() => toggleClassFilter(classId)}

            >

              {label}

            </button>

          ))}

        </div>

      ) : null}



      {filterNotice ? (

        <p className="small text-muted mb-2 px-1">

          {t(prefix + 'scheduleWarningsFilterNotice')}

        </p>

      ) : null}



      {filteredConflicts.length === 0 ? (

        <p className="small text-muted mb-0 px-1">

          {t(prefix + 'scheduleConflictsFilterEmpty')}

        </p>

      ) : (

        <div className="schedule-conflicts-panel__list" role="list">

          {groupedByDay.map(([dayKey, dayConflicts]) => (

            <div key={dayKey} className="mb-2">

              <div className="schedule-conflicts-panel__day-heading text-muted fw-semibold mb-1">

                {t(prefix + 'scheduleSettingsDayLabel', { day: dayKey + 1 })}

              </div>

              <ul className="list-unstyled mb-0">

                {dayConflicts.map((conflict) => {

                  const [idA, idB] = conflict.matchIds;

                  const matchA = matchById.get(idA);

                  const matchB = matchById.get(idB);

                  const isError = conflict.severity === 'error';

                  const rowText = formatScheduleConflictMessage(
                    conflict,
                    matchA,
                    matchB,
                    (match) => formatMatchSlot(match, timeZone),
                    t
                  );



                  return (

                    <li key={conflict.id} role="listitem">

                      <button

                        type="button"

                        className="schedule-conflicts-panel__row btn btn-link text-start w-100 text-decoration-none text-body px-0 py-1 d-flex gap-2 align-items-center"

                        onClick={() => handleRowClick(conflict)}

                      >

                        <span className="schedule-conflicts-panel__row-icon flex-shrink-0">

                          {isError ? (

                            <ExclamationCircleFill className="text-danger" size={16} aria-hidden />

                          ) : (

                            <ExclamationTriangleFill

                              className="text-warning"

                              size={16}

                              aria-hidden

                            />

                          )}

                        </span>

                        <span className="schedule-conflicts-panel__row-text small flex-grow-1 min-w-0">

                          {rowText}

                        </span>

                        <span className="schedule-conflicts-panel__row-hint small text-muted flex-shrink-0 d-flex align-items-center gap-1">

                          {t(prefix + 'scheduleConflictRowDetailsHint')}

                          <ChevronRight size={12} aria-hidden />

                        </span>

                      </button>

                    </li>

                  );

                })}

              </ul>

            </div>

          ))}

        </div>

      )}



      <ScheduleConflictDetailModal

        show={selectedConflict != null}

        conflict={selectedConflict}

        matchById={matchById}

        timeZone={timeZone}

        classIdToObject={classIdToObject}

        onClose={() => setSelectedConflict(null)}

        onFocusOnGrid={handleModalFocusOnGrid}

        onViewMatch={onViewConflictMatch}

        formatMatchSlot={formatMatchSlot}

      />

    </div>

  );

});

